import { PickingInfo } from '@deck.gl/core/typed';
import { QuadkeyLayer, TileLayer, _WMSLayer as WMSLayer } from '@deck.gl/geo-layers/typed';
import { BitmapLayer, GeoJsonLayer } from '@deck.gl/layers/typed';
import { GetMapLayerConfigsQuery, MapLayerType } from '@kinetic-ui/data-layer';
import { hexToRgbArray } from 'kineticUtils/maps';
import { isArray } from 'lodash';
import reduce from 'lodash/reduce';
import { StyleSpecification } from 'maplibre-gl';
import { LayerProps, Layer as MapLibreLayer, Source } from 'react-map-gl/maplibre';
import { GoogleMapType } from './types';
import { ColorData, GOOGLE_SESSION_MAP } from './useDeckGlTileLayers';

export const getMapLibreVectorLayer = (
  mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0]
) => {
  const layerProps: LayerProps = {
    id: mapConfig.id,
    type: mapConfig.data.type,
    filter: getFilter(mapConfig),
    source: mapConfig.id,
    'source-layer': mapConfig.data.sourceLayer,
    paint: getPaint(mapConfig),
  };
  return (
    <Source
      key={mapConfig.id}
      id={mapConfig.id}
      type='vector'
      maxzoom={parseInt(mapConfig.data.maxZoom)}
      minzoom={parseInt(mapConfig.data.minZoom)}
      tiles={[mapConfig.data.baseUrl]}
    >
      <MapLibreLayer {...layerProps} />
    </Source>
  );
};
const getFillColor = (mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0], layerData: any) => {
  const multipleFillColorData = mapConfig.data.multipleFillColors;
  let fillColor = mapConfig.data.fillColor;

  multipleFillColorData?.forEach((colorData: ColorData) => {
    const propertyValue = colorData.propertyName
      ?.split('.')
      .reduce((prev, current) => prev?.[current], layerData.properties);

    if (propertyValue === null || propertyValue === undefined) {
      return;
    }
    if (
      propertyValue === colorData.propertyValue ||
      (+propertyValue >= colorData.propertyValue?.[0] &&
        +propertyValue < colorData.propertyValue?.[1])
    ) {
      fillColor = colorData.propertyColor;
    }
  });
  return fillColor ? hexToRgbArray(fillColor) : undefined;
};
const getStrokeColor = (
  mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0],
  layerData: any
) => {
  const multipleStrokeColorData = mapConfig.data.multipleStrokeColors;
  let strokeColor = mapConfig.data.strokeColor;

  multipleStrokeColorData?.forEach((colorData: ColorData) => {
    const propertyValue = colorData.propertyName
      ?.split('.')
      .reduce((prev, current) => prev?.[current], layerData.properties);

    if (propertyValue === null || propertyValue === undefined) {
      return;
    }
    if (
      propertyValue === colorData.propertyValue ||
      (+propertyValue >= colorData.propertyValue?.[0] &&
        +propertyValue < colorData.propertyValue?.[1])
    ) {
      strokeColor = colorData.propertyColor;
    }
  });
  return strokeColor ? hexToRgbArray(strokeColor) : undefined;
};
const getLayerTooltip = (pickingInfo: PickingInfo, tooltip?: string) => {
  const layerData = pickingInfo.object;
  if (!layerData || !tooltip?.length) {
    return null;
  }
  const tooltipTemplateSplit = tooltip.split('}');
  const tooltipPropertyNames: string[] = [];
  let updatedTooltip = tooltip;

  tooltipTemplateSplit.forEach((splitTooltip) => {
    const propertyNameFirstIndex = splitTooltip.indexOf('${');
    if (propertyNameFirstIndex >= 0) {
      tooltipPropertyNames.push(splitTooltip.slice(propertyNameFirstIndex + 2));
    }
  });

  tooltipPropertyNames.forEach((tooltipProperty) => {
    const propertyValue = tooltipProperty
      .split('.')
      .reduce((prev, current) => prev?.[current], layerData.properties);

    if (propertyValue !== undefined) {
      updatedTooltip = updatedTooltip.replace(
        `${'${' + tooltipProperty}}`,
        typeof propertyValue === 'object' ? JSON.stringify(propertyValue) : propertyValue
      );
    }
  });

  return updatedTooltip;
};
type GoogleLayerSessionResponse = {
  session: string;
  expiry: string;
  tileWidth: number;
  tileHeight: number;
  imageFormat: string;
};
export const getGoogleLayerUrl = async (
  apiKey: string,
  mapType: GoogleMapType,
  region: string,
  language: string
) => {
  const baseUrl = `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?key=${apiKey}`;
  const key = `${apiKey}_${mapType}`;
  if (
    GOOGLE_SESSION_MAP.has(key) &&
    GOOGLE_SESSION_MAP.get(key)!.expiry * 1000 > Date.now() - 30000
  ) {
    return `${baseUrl}&session=${GOOGLE_SESSION_MAP.get(key)!.session}`;
  }
  const sessionUrl = `https://tile.googleapis.com/v1/createSession?key=${apiKey}`;
  const response = await fetch(sessionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      mapType,
      language,
      region,
    }),
  });
  try {
    const data: GoogleLayerSessionResponse = await response.json();
    GOOGLE_SESSION_MAP.set(key, {
      session: data.session,
      expiry: parseInt(data.expiry),
    });
    return `${baseUrl}&session=${data.session}`;
  } catch (e) {
    console.error('Error getting google session', e);
    return '';
  }
};

export const mapConfigToLayer = async (
  mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0],
  currentZoom?: number
) => {
  switch (mapConfig.type) {
    case MapLayerType.Xyz:
      return new TileLayer({
        visible: true,
        id: mapConfig.id,
        data: mapConfig.data.baseUrl,
        pickable: false,
        minZoom: parseInt(mapConfig.data.minZoom),
        maxZoom: parseInt(mapConfig.data.maxZoom),
        tileSize: parseInt(mapConfig.data.tileSize ?? '512'),
        zoomOffset: 0,
        renderSubLayers: (props: any) => {
          const {
            bbox: { west, south, east, north },
          } = props.tile;
          return [
            new BitmapLayer(props, {
              data: null as never,
              image: props.data,
              bounds: [west, south, east, north],
            }),
          ];
        },
        order: 0,
      });
    case MapLayerType.Wms:
      const template = {
        LAYERS: '{layers}',
        BBOX: '{east},{north},{west},{south}',
        TRANSPARENT: true,
        FORMAT: 'image/png',
        REQUEST: 'GetMap',
        SERVICE: 'WMS',
        WIDTH: '{width}',
        HEIGHT: '{height}',
        VERSION: '1.3.0',
        CRS: 'EPSG:3857',
        STYLES: '',
      };

      const qs = reduce(
        template,
        (prev: any[], curr, key) => {
          prev.push(`${key}=${curr}`);
          return prev;
        },
        []
      ).join('&');

      return new WMSLayer({
        visible: true,
        id: mapConfig.id,
        data: `${ensureQuestionMark(mapConfig.data.baseUrl)}${qs}`,
        layers: [mapConfig.data.layerId],
        serviceType: 'template',
        minZoom: parseInt(mapConfig.data.minZoom),
        maxZoom: parseInt(mapConfig.data.maxZoom),
        order: 0,
      });
    case MapLayerType.Quadkey:
      return new QuadkeyLayer({
        visible: true,
        id: mapConfig.id,
        data: mapConfig.data.baseUrl,
        minZoom: parseInt(mapConfig.data.minZoom),
        maxZoom: parseInt(mapConfig.data.maxZoom),
        order: 0,
      });
    case MapLayerType.Geojson:
      const { height, width } = await getImageDimensions(mapConfig.data.icon);
      const [minZoom, maxZoom] = [
        parseInt(mapConfig.data.minZoom),
        parseInt(mapConfig.data.maxZoom),
      ];

      const visible =
        currentZoom === undefined ? true : currentZoom > minZoom && currentZoom < maxZoom;
      return new GeoJsonLayer({
        visible,
        id: `${mapConfig.id}_${visible}`,
        data: mapConfig.data.baseUrl,

        getFillColor: (layerData) => getFillColor(mapConfig, layerData)!,
        lineWidthMinPixels: mapConfig?.data?.minimumStrokeWidth
          ? parseInt(mapConfig.data.minimumStrokeWidth)
          : 0,
        getLineWidth: mapConfig?.data?.strokeWidth ? parseInt(mapConfig.data.strokeWidth) : 1,
        getLineColor: (layerData) => getStrokeColor(mapConfig, layerData)!,
        opacity: mapConfig.data.fillOpacity ? parseFloat(mapConfig.data.fillOpacity) / 100 : 1,
        _subLayerProps: {
          'polygons-stroke': {
            opacity: mapConfig.data.opacity ? parseFloat(mapConfig.data.opacity) / 100 : 1,
          },
        },
        pointType: mapConfig.data.icon ? 'icon' : 'circle',
        getIcon: (layerData: any) => {
          const icon = mapConfig.data.icon;
          if (!icon) {
            return undefined;
          }
          let url = mapConfig.data.icon;
          if (isArray(icon)) {
            url = mapConfig.data.icon[0];
          }
          return {
            id: `${mapConfig.id}_${layerData?.geometry?.coordinates}Icon`,
            anchorY: mapConfig.data.iconY,
            anchorX: mapConfig.data.iconX,
            height: height,
            width: width,
            url,
          };
        },
        getIconSize: () => width,
        getLayerTooltip: (info: PickingInfo) => getLayerTooltip(info, mapConfig.data.tooltip),
        order: !mapConfig.data.order ? 0 : mapConfig.data.order / 10,
        pickable: true,
      });
  }
};
function getImageDimensions(url: string): Promise<{
  width: number;
  height: number;
}> {
  if (!url) {
    return Promise.resolve({ width: 0, height: 0 });
  }
  return new Promise(function (resolved) {
    var i = new Image();
    i.onload = function () {
      resolved({ width: i.width, height: i.height });
    };
    i.src = url;
  });
}
export const mapConfigToStyle = (
  mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0],
  baseUrl?: string
): StyleSpecification | undefined => {
  if (!mapConfig) {
    return undefined;
  }
  const tileSize = mapConfig.data.tileSize ?? 512;
  const tileUrl = baseUrl ?? mapConfig.data.baseUrl;
  if (!tileUrl) {
    return undefined;
  }
  return {
    version: 8,
    sources: {
      [mapConfig.id]: {
        tileSize,
        type: 'raster',
        tiles: [tileUrl],
      },
    },
    layers: [
      {
        source: mapConfig.id,
        id: mapConfig.id,
        type: 'raster',
      },
    ],
  };
};
const getPaint = (mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0]) => {
  try {
    return JSON.parse(mapConfig.data.paint);
  } catch (e) {
    console.error(`Error parsing paint in mapConfig: ${mapConfig.name}`, e);
    return {};
  }
};
const getFilter = (mapConfig: GetMapLayerConfigsQuery['mapLayerConfigs'][0]) => {
  try {
    return JSON.parse(mapConfig.data.filter);
  } catch (e) {
    console.error(`Error parsing filter in mapConfig: ${mapConfig.name}`, e);
    return {};
  }
};
function ensureQuestionMark(url: string): string {
  const trimmedUrl = url.trim();
  if (trimmedUrl.endsWith('?')) {
    return trimmedUrl;
  }
  return `${trimmedUrl}?`;
}
